import { Link } from 'react-router';
import { Button, SegmentGroup } from 'semantic-ui-react';
import { SuiTable, TitleSegment } from '@britishcouncil/react-admin';
import { usePermissions } from '@britishcouncil/react-common';

import { Permission, WritingCertificationListItem } from 'api';
import { NextDateLabel, useExaminerId } from 'components';
import { useTableState } from 'hooks';
import { useWritingCertificationListQuery, useWritingNextCertDateQuery } from 'store';
import { TableState } from 'types';
import { formatDate } from 'utils';
import { COLUMNS } from './WritingCertificationList.columns';

const EMPTY_LIST: WritingCertificationListItem[] = [];

const INITIAL_TABLE_STATE: TableState<WritingCertificationListItem, undefined> = {
  pageIndex: 0,
  pageSize: 50,
  sortBy: [],
  filters: undefined,
};

export const WritingCertificationList = () => {
  const examinerId = useExaminerId();
  const { permissions } = usePermissions();
  const { tableState, setTableState } = useTableState(INITIAL_TABLE_STATE);

  const canAdd = permissions.includes(Permission.UpdateCertificationHistory);

  const { data, isFetching } = useWritingCertificationListQuery({
    examinerId,
    page: tableState.pageIndex + 1,
    count: tableState.pageSize,
  });

  const { data: nextCertDate } = useWritingNextCertDateQuery({ examinerId });

  return (
    <SegmentGroup>
      <TitleSegment
        leftSection={
          nextCertDate ? (
            <NextDateLabel
              label="Next Writing Recertification due date"
              value={formatDate(nextCertDate)}
              testId="certification-next-date"
            />
          ) : (
            <>&nbsp;</>
          )
        }
        rightSection={
          canAdd ? <Button primary as={Link} to="add" icon="plus" content="Add new" /> : undefined
        }
      />
      <SuiTable
        columns={COLUMNS}
        data={data?.results ?? EMPTY_LIST}
        disableFilters
        disableSortBy
        onStateChange={setTableState}
        loading={isFetching}
        state={tableState}
        totalCount={data?.count}
        noResultsMsg="No certifications found"
        tableProps={{ 'data-testid': 'certification-list-table' }}
      />
    </SegmentGroup>
  );
};
